import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core';
import { MAT_PREFIX, MAT_SUFFIX, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MaterialIcons } from '../domain/mat-icons.const';
import { CustomIcons, rmaIcons } from '../domain/rma-icons.const';
export type RmaIcons = CustomIcons | MaterialIcons;

const ICON_CONFIG = {
  extraSmall: {
    size: 12,
    padding: 4,
  },
  small: {
    size: 18,
    padding: 8,
  },
  normal: {
    size: 24,
    padding: 8,
  },
  large: {
    size: 32,
    padding: 8,
  },
  extraLarge: {
    size: 48,
    padding: 10,
  },
  orb: {
    size: 40,
    padding: 12,
  },
} as const;

export type IconSize = keyof typeof ICON_CONFIG;
export type IconTheme =
  | 'primary'
  | 'primary-alt'
  | 'discovery'
  | 'caution'
  | 'inactive'
  | 'success'
  | 'warning'
  | 'star'
  | 'custom'
  | 'upsell';

@Component({
  selector: 'rma-icon',
  imports: [MatIconModule],
  templateUrl: './ui-icon.component.html',
  styleUrls: ['./ui-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'theme()',
    '[class.icon-rounded]': 'rounded()',
    '[class.form-field-padding]': 'formFieldPadding',
    '[class.icon-button-padding-left]': 'iconButtonPaddingLeft',
    '[class.icon-button-padding-right]': 'iconButtonPaddingRight',
    '[style.--rma-icon-fill]': 'fill()',
    '[style.--rma-icon-size]': 'iconSize()',
    '[style.--rma-padding-size]': 'paddingSize()',
  },
})
export class RmaIconComponent {
  private readonly elRef = inject(ElementRef);
  private readonly matPrefix = inject<MatPrefix>(MAT_PREFIX, { optional: true });
  private readonly matSuffix = inject<MatSuffix>(MAT_SUFFIX, { optional: true });

  protected readonly fontSet = 'material-symbols-outlined';

  // Form-field and icon-button padding depend on host attributes and on whether the icon was
  // projected into a mat-form-field prefix/suffix slot. Neither can change after construction,
  // so they are read once rather than derived reactively.
  protected readonly formFieldPadding = this.matPrefix !== null || this.matSuffix !== null;
  protected readonly iconButtonPaddingLeft = this.hasAttribute('iconPositionEnd');
  protected readonly iconButtonPaddingRight = this.hasAttribute('matButtonIcon') && !this.hasAttribute('iconPositionEnd');

  protected readonly iconType = computed<'material' | 'custom'>(() =>
    rmaIcons.some(({ name }) => name === this.glyph()) ? 'custom' : 'material',
  );

  // legacySize overrides the glyph size only; padding is always driven by `size`, matching the
  // ordering the previous ngOnChanges produced.
  protected readonly iconSize = computed(() => {
    const legacySize = this.legacySize();

    if (legacySize) {
      return legacySize;
    }

    return `${ICON_CONFIG[this.size() ?? 'normal'].size}px`;
  });

  protected readonly paddingSize = computed(() => `${ICON_CONFIG[this.size() ?? 'normal'].padding}px`);

  public readonly glyph = input.required<RmaIcons>();

  public readonly fill = input<0 | 1>(0);

  public readonly inline = input(false);

  public readonly theme = input<IconTheme>();

  public readonly rounded = input(false);

  public readonly size = input<IconSize>('normal');
  /**
   * @deprecated: This was to aid migration to new icon component and should not be used for new work
   */
  public readonly legacySize = input<string>();

  private hasAttribute(attribute: string) {
    return this.elRef.nativeElement.getAttribute(attribute) !== null;
  }
}
